/**
 * UserEditModal Component
 * Modal for editing user roles and profile information
 * Includes role management with confirmations and self-demotion prevention
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfile } from '../../lib/types/user';
import {
  adminQueryKeys,
  useAdminUserEntitlements,
} from '../../hooks/useAdminApi';
import { authenticatedApi } from '../../lib/authenticated-api';
import {
  applyManualEntitlementChanges,
  deriveManualEntitlementIds,
} from '../../lib/manualEntitlementChanges';
import { UserAvatar } from '../ui/UserAvatar';
import { FormButton } from '../form/FormButton';
import { UserBadgeSection } from './UserBadgeSection';
import { UserAgreementsSection } from './UserAgreementsSection';
import {
  EntitlementEditor,
  type ManualEntitlementSaveRequest,
} from './EntitlementEditor';
import { EntitlementProvenanceList } from './EntitlementProvenanceList';
import { UserSubscriptionsSection } from './UserSubscriptionsSection';
import { appConfig } from '../../lib/env';
import { stripeCustomerDashboardUrl } from '../../lib/stripe';

interface UserEditModalProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
}

export function UserEditModal({ user, isOpen, onClose }: UserEditModalProps) {
  const queryClient = useQueryClient();
  // The editor's snapshot: the manual ids from the fetch made at open. `null`
  // while the editor is closed; the editor is mounted only while it is set.
  const [editorSnapshot, setEditorSnapshot] = useState<string[] | null>(null);
  const [openingEditor, setOpeningEditor] = useState(false);
  const [openEditorError, setOpenEditorError] = useState<string | null>(null);

  // Fetch user's active entitlements when modal is open.
  //
  // `isLoading` and `isError` are read, not just `data`, because
  // `EntitlementProvenanceList` renders `undefined` as "No active entitlements"
  // — so without them a request that is still in flight, or that failed,
  // displays as a positive finding that this user holds no access. On the
  // section the card labels as the authorization truth, that is the worst
  // available failure mode.
  const {
    data: entitlementsData,
    isLoading: entitlementsLoading,
    isError: entitlementsError,
    error: entitlementsErrorObj,
    refetch: refetchEntitlements,
  } = useAdminUserEntitlements(user.uid, isOpen);

  // The editor is not seeded from this cached read: opening it refetches, and
  // it snapshots that result (see `handleOpenEditor`). The button still waits
  // for this read, because the card beside it would otherwise be describing
  // access nobody has loaded yet.
  //
  // `isError` alone is not the question. On a background refetch failure v5
  // KEEPS the last good `data` while setting `isError`, and this panel used to
  // then say "nothing about their access is known here" while the billing
  // panel below — handed the same retained data — rendered "Granting now: …".
  // Two panels contradicting each other, in the one state this card exists to
  // make legible. So the hard failure is "errored AND nothing to show"; a
  // failure with data retained degrades to a staleness warning instead.
  const entitlementsUnavailable = entitlementsError && !entitlementsData;
  const entitlementsStale = entitlementsError && !!entitlementsData;
  const canEditEntitlements =
    !entitlementsLoading && !entitlementsUnavailable && !openingEditor;

  // Open on a FRESH snapshot (#2029). The cached read can be minutes old, and
  // a save closes the editor before its invalidation refetch lands, so opening
  // straight from the cache could freeze pre-save data. The fetch is judged by
  // its `status`, not by `data`: a failed refetch keeps the old data.
  //
  // The snapshot is the editor's INPUT (what the admin can grant or revoke
  // manually), and is intentionally distinct from the read-only provenance
  // list below, which shows all active access across every source.
  const handleOpenEditor = async () => {
    setOpeningEditor(true);
    setOpenEditorError(null);
    try {
      const result = await refetchEntitlements();
      if (result.status !== 'success') {
        setOpenEditorError(
          result.error instanceof Error ? result.error.message : 'unknown error'
        );
        return;
      }
      setEditorSnapshot(deriveManualEntitlementIds(result.data));
    } finally {
      setOpeningEditor(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Sends only the admin's toggles, one grant at a time, through the per-grant
  // routes (#2028); never the whole set. Both queries are invalidated after
  // every attempt, successful or not, so the rest of the card is current. The
  // responses are never written into the cache.
  const handleSaveEntitlements = async ({
    ops,
    reason,
    onProgress,
    previous,
    signal,
  }: ManualEntitlementSaveRequest) => {
    try {
      return await applyManualEntitlementChanges(
        authenticatedApi,
        user.uid,
        ops,
        reason,
        onProgress,
        { previous, signal }
      );
    } finally {
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.user(user.uid),
      });
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.userEntitlements(user.uid),
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal */}
        <div
          data-testid="user-edit-modal"
          className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            {/*
              Named for the user, not for the verb. The card is mostly a
              read-only diagnosis, there is no Save/Cancel — every control
              saves as you use it — and "Edit User" over a Close button left a
              first-time admin unsure whether their change had stuck.
            */}
            <div className="min-w-0">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.display_name || user.email || 'User record'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Changes save as you make them.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <UserAvatar
              displayName={user.display_name}
              photoUrls={user.photo_url}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.display_name}
              </p>
              {user.email && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate font-mono">
                {user.uid}
              </p>
              {user.stripe_customer_id && (
                <p className="mt-1 flex items-center gap-2 text-xs min-w-0">
                  <a
                    href={stripeCustomerDashboardUrl(user.stripe_customer_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stripe-customer-link"
                    className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                  >
                    View in Stripe
                    {appConfig.environment !== 'production' ? ' (test)' : ''}
                    <svg
                      aria-hidden="true"
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                  <span className="font-mono text-gray-400 dark:text-gray-500 truncate">
                    {user.stripe_customer_id}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Access State — what this user can actually do, by source. This is
              the authorization truth; the billing section below is not. */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Access
                </h4>
                {/*
                  One line, and it states the rule rather than hinting at it: a
                  row disappears only when it has ended from EVERY source, which
                  is why a live entitlement can still carry a "· ended" chip.
                  An earlier draft said "access that has ended isn't shown" and
                  read as contradicting the chips two lines below it.
                */}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  What this user can do right now, and how they got it. Access
                  only disappears from this list once it has ended from every
                  source.
                </p>
              </div>
              <div className="shrink-0">
                <FormButton
                  type="button"
                  variant="outline"
                  disabled={!canEditEntitlements}
                  onClick={() => {
                    void handleOpenEditor();
                  }}
                >
                  {openingEditor ? 'Loading…' : 'Add or remove manual access'}
                </FormButton>
              </div>
            </div>

            {openEditorError && (
              <p
                role="alert"
                className="text-sm text-red-600 dark:text-red-400 mb-2"
              >
                Couldn&apos;t load current access, so the editor didn&apos;t
                open. ({openEditorError})
              </p>
            )}

            {entitlementsLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading entitlements…
              </p>
            ) : entitlementsUnavailable ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                <strong>Couldn&apos;t load access.</strong> This doesn&apos;t
                mean the user has none — we just don&apos;t know. Editing is off
                until it loads. (
                {entitlementsErrorObj instanceof Error
                  ? entitlementsErrorObj.message
                  : 'unknown error'}
                )
              </p>
            ) : (
              <>
                {entitlementsStale && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                    Couldn&apos;t load recent changes — showing the last known
                    state. (
                    {entitlementsErrorObj instanceof Error
                      ? entitlementsErrorObj.message
                      : 'unknown error'}
                    )
                  </p>
                )}
                <EntitlementProvenanceList entitlements={entitlementsData} />
              </>
            )}
          </div>

          {/* Billing State — the observational provider mirror. Display only. */}
          <UserSubscriptionsSection
            uid={user.uid}
            entitlements={entitlementsData}
          />

          {/* Badges and Agreements last: neither answers "why can't this
              customer get in?", and they used to hold the top of the card. */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <UserBadgeSection user={user} />
          </div>

          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <UserAgreementsSection user={user} />
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-6">
            <FormButton type="button" variant="outline" onClick={handleClose}>
              Close
            </FormButton>
          </div>
        </div>
      </div>

      {/* Entitlement Editor Modal: mounted only while open, one instance per
          open, so it snapshots the fresh fetch and nothing older. */}
      {editorSnapshot && (
        <EntitlementEditor
          user={user}
          onClose={() => setEditorSnapshot(null)}
          onSave={handleSaveEntitlements}
          snapshot={editorSnapshot}
        />
      )}
    </div>
  );
}
